import React, { useEffect } from 'react';
import { OrderAutomatizaionRow } from '@/Types';
import styles from './ShipCheaperLogistic.scss';
import { Tooltip } from '@/Components/Tooltip/Tooltip';
import { PackageSelect } from '../_Shared/PackageSelect/PackageSelect';
import { LogisticProductSelect } from '../_Shared/LogisticProductSelect/LogisticProductSelect';
import { LogisticProductInfo } from '../_Shared/LogisticProductInfo/LogisticProductInfo';
import { useLocalization } from '@/Hooks';
import { Button } from '@/Components/Controls/Button/Button';
import { BUTTON_THEMES } from '@/Components/Controls/Button/ButtonThemes';
import { globalHistory } from '@/GlobalHistory';
import { PagesRouting } from '@/Routing';
import { useContractLogisticOptions } from '../../hooks/useContractLogisticOptions';
import { useContractLogisticTooltips } from '../../hooks/useContractLogisticTooltips';
import { FormData } from '@/ModalWindows/OrderAutomatizationModal/hooks/useOrderAutomatizationModalForm';
import { useShipCheaperLogisticContract } from './hooks/useShipCheaperLogisticContract';
import { ModalDialog } from '@/Components/ModalDialog/ModalDialog';
import { stringUtils } from '@/Utils';
import { useLogisticContractForm } from '../../hooks/useLogisticContractForm';

type Props = {
  rowIndex: number;
  rowData: OrderAutomatizaionRow;
};

export const ShipCheaperLogistic = ({ rowIndex, rowData }: Props) => {
  const {
    modalWindows: {
      orderLinkWithProduct: {
        ShippingContract: { Activate, NoActivatedLogistics, NoActivatedPackages },
        ContractDisabledModal,
      },
    },
    buttons: { Yes, No },
  } = useLocalization();

  const {
    rules: { required },
    methods: { register, errors },
    packages: { packages, append, remove },
    logisticProduct: { logisticProduct, onSelectProduct, onResetProduct },
  } = useLogisticContractForm(rowIndex);

  // Options
  const defaultCurrency = '€';
  const { productTooltipMessage } = useContractLogisticTooltips(packages);
  const { packageOptions, productOptions, isContractsConfigured } = useContractLogisticOptions(
    rowData.orderId,
    packages,
    false,
  );
  const { contractInfo, showWarningModal, setShowWarningModal, onRedirect } = useShipCheaperLogisticContract(rowData);

  useEffect(() => {
    onResetProduct();
  }, [productOptions.length]);

  if (!packageOptions) return null;

  if (!isContractsConfigured || packageOptions.length == 0) {
    return (
      <div className={styles.logisticRow}>
        <div className={styles.noLogisticsMessage}>
          <span>{isContractsConfigured ? NoActivatedPackages : NoActivatedLogistics}</span>
          <Button
            text={Activate}
            theme={BUTTON_THEMES.PRIMARY}
            onClick={() =>
              globalHistory.push(
                isContractsConfigured
                  ? PagesRouting.SettingsPages.PackagesSettings
                  : PagesRouting.SettingsPages.LogisticSettings,
              )
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.logisticRow}>
      {showWarningModal && (
        <ModalDialog
          title={ContractDisabledModal.Title}
          text={stringUtils.insertParamsToString(ContractDisabledModal.Text, [
            String(contractInfo?.warehouseLocationName),
          ])}
          onOk={onRedirect}
          onClose={() => setShowWarningModal(false)}
          okButtonText={Yes}
          cancelButtonText={No}
        />
      )}

      {packages.map((p, idx) => (
        <PackageSelect
          key={p.array_id}
          index={idx}
          rowIndex={rowIndex}
          isAddActive={packages.length < 5}
          onAdd={() => append({ key: '', weight: '' })}
          onDelete={() => remove(idx)}
          packages={packageOptions}
          disabled={rowData.hasLabelCreated}
          isOwnContract={false}
          orderId={rowData.orderId}
        />
      ))}

      {!logisticProduct ? (
        <Tooltip text={productTooltipMessage} shouldShowOnHover={productOptions.length == 0}>
          <LogisticProductSelect
            name={(`prepareLabels.${rowIndex}.userProductId` as unknown) as keyof FormData}
            register={register}
            rules={required}
            error={errors}
            products={productOptions}
            currency={defaultCurrency}
            onSelect={(product) => {
              if (contractInfo?.contract) {
                if (contractInfo?.contract?.isActive) {
                  onSelectProduct(product);
                } else {
                  setShowWarningModal(true);
                }
              }
            }}
            readonly={rowData.hasLabelCreated}
          />
        </Tooltip>
      ) : (
        <LogisticProductInfo product={logisticProduct} currency={defaultCurrency} onDelete={onResetProduct} />
      )}
    </div>
  );
};
